import React, { useMemo } from "react";
import { Field } from "react-final-form";
import InputText from "@components/Form/InputText";
import Select from "@components/Form/Select";
import {
  composeFormValidators,
  isRequired,
  isAlphaNumeric,
  isEmail,
  isPhoneNumber,
  hasMinLength,
  hasMaxLength,
  isCNP,
  hasMaxVal,
  hasMinVal,
} from "@lib/validations";
import SelectSearch from "@components/Form/SelectSearch";

const INPUT_CLASS = "!max-w-full !min-w-0";

const OwnerDataSection = ({ apiData, form }) => {
  const { counties, cities } = apiData;

  const { values } = form.getState();

  const selectedCountyId = values?.userDataSection?.address?.countyId;

  const countyOptions = useMemo(
    () =>
      counties &&
      counties.map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [counties]
  );

  const cityOptions = useMemo(
    () =>
      cities?.[selectedCountyId]
        ? cities[selectedCountyId].map((city) => ({
            value: city.id,
            label: city.name,
          }))
        : [],
    [cities, selectedCountyId]
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
      <div>
        <Field
          name="userDataSection.lastName"
          component={InputText}
          label="Nume"
          required
          placeholder="Introdu numele complet"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(
            isRequired(),
            isAlphaNumeric(),
            hasMaxLength(50)
          )}
        />
      </div>

      <div>
        <Field
          name="userDataSection.firstName"
          component={InputText}
          label="Prenume"
          required
          placeholder="Introdu prenumele complet"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(
            isRequired(),
            isAlphaNumeric(),
            hasMaxLength(50)
          )}
        />
      </div>

      <div>
        <Field
          name="userDataSection.cif"
          component={InputText}
          label="CNP"
          required
          placeholder="Introdu CNP"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(
            isRequired(),
            isCNP(),
            hasMinLength(13),
            hasMaxLength(13)
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 col-span-1">
        <div>
          <Field
            name="userDataSection.idSerial"
            component={InputText}
            label="Serie CI/BI"
            required
            placeholder="AB"
            inputClassName={INPUT_CLASS}
            labelClass="text-sm"
            format={(value) => value?.toUpperCase()}
            validate={composeFormValidators(
              isRequired(),
              hasMaxLength(2),
              hasMinLength(2)
            )}
          />
        </div>

        <div>
          <Field
            name="userDataSection.idNumber"
            component={InputText}
            label="Numar CI/BI"
            required
            placeholder="123456"
            inputClassName={INPUT_CLASS}
            labelClass="text-sm"
            validate={composeFormValidators(
              isRequired(),
              hasMinLength(6),
              hasMaxLength(7)
            )}
          />
        </div>
      </div>

      <div>
        <Field
          name="userDataSection.address.countyId"
          validate={composeFormValidators(isRequired())}
        >
          {({ input, meta }) => (
            <Select
              input={{
                ...input,
                value: input.value || "",
                onChange: async (e) => {
                  const countyId = e.target.value;
                  input.onChange(countyId);
                  form.change("userDataSection.address.cityId", null);
                },
              }}
              meta={meta}
              label="Judet"
              required
              inputClassName={INPUT_CLASS}
              labelClass="text-sm"
            >
              <option value="">alege...</option>

              {counties &&
                countyOptions.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
            </Select>
          )}
        </Field>
      </div>

      <div>
        <Field
          name="userDataSection.address.cityId"
          component={SelectSearch}
          label="Localitate"
          required
          validate={composeFormValidators(isRequired())}
          controlClassName={INPUT_CLASS}
          labelClass="text-sm"
          placeholder="Cauta localitatea"
          disabled={!cities?.[selectedCountyId]}
          parse={(option) => option.value}
          format={(value) =>
            cityOptions.find(
              (option) => String(option.value) === String(value)
            ) || null
          }
          options={cityOptions || []}
        />
      </div>

      <div>
        <Field
          name="userDataSection.address.streetName"
          component={InputText}
          label="Adresa"
          required
          placeholder="Introdu adresa completa"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(isRequired(), hasMaxLength(300))}
        />
      </div>

      <div>
        <Field
          name="userDataSection.address.postalCode"
          component={InputText}
          label="Cod postal"
          placeholder="Introdu cod postal"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          required
          validate={composeFormValidators(
            isRequired(),
            hasMaxLength(6),
            hasMinLength(6)
          )}
        />
      </div>

      <div>
        <Field
          name="userDataSection.email"
          component={InputText}
          type="email"
          label="Email"
          required
          placeholder="Introdu email"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(isRequired(), isEmail())}
        />
      </div>

      <div>
        <Field
          name="userDataSection.phone"
          component={InputText}
          type="tel"
          label="Telefon"
          required
          placeholder="Introdu telefon"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(isRequired(), isPhoneNumber())}
        />
      </div>

      <div>
        <Field
          name="userDataSection.idExpirationDate"
          component={InputText}
          type="number"
          label="An expirare CI/BI"
          required
          placeholder={new Date().getFullYear().toString()}
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          parse={(value) =>
            value?.includes("-")
              ? new Date(value).getFullYear().toString()
              : value
          }
          format={(value) =>
            value?.includes("-")
              ? new Date(value).getFullYear().toString()
              : value
          }
          validate={composeFormValidators(
            isRequired(),
            hasMinVal(new Date().getFullYear()),
            hasMaxLength(4),
            hasMinLength(4)
          )}
        />
      </div>

      <div>
        <Field
          name="userDataSection.driverLicenceDate"
          component={InputText}
          placeholder={new Date().getFullYear().toString()}
          label="An obtinere permis"
          type="number"
          required
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          parse={(value) =>
            value?.includes("-")
              ? new Date(value).getFullYear().toString()
              : value
          }
          format={(value) =>
            value?.includes("-")
              ? new Date(value).getFullYear().toString()
              : value
          }
          validate={composeFormValidators(
            isRequired(),
            hasMaxVal(new Date().getFullYear()),
            hasMaxLength(4),
            hasMinLength(4)
          )}
        />
      </div>
    </div>
  );
};

export default OwnerDataSection;
